import { ListSkeleton, SectionCard } from '@atlas/ui-components'
import { Button, Chip, cn } from '@atlas/ui-primitives'
import { Archive, Pencil } from 'lucide-react'
import { useParams } from 'react-router-dom'
import { ChipSet } from '@/components/classification/ChipSet'
import { ConfirmDialog } from '@/components/confirm/ConfirmDialog'
import { BackLink } from '@/components/editorial/BackLink'
import { Eyebrow, PageHeader } from '@/components/editorial/PageHeader'
import { EditRulePropertiesForm } from '@/components/rules/EditRulePropertiesForm'
import { RuleSeverityPill } from '@/components/rules/RuleSeverityPill'
import type { Facet } from '@/domain/library.types'
import {
	RULE_ELEMENT_TYPE_LABEL,
	RULE_ELEMENT_TYPE_ORDER,
	RULE_LIFECYCLE_COLOR,
	RULE_LIFECYCLE_LABEL,
	type Rule,
	RuleLifecycle,
	ruleTypeLabel,
	scopeLabel,
} from '@/domain/rules.types'
import { railAxesToFacets, useAxesWithValues } from '@/hooks/useLibrary'
import { useRule } from '@/hooks/useRules'
import { useCan } from '@/lib/capabilities'
import { REFUSAL, Restricted } from '@/lib/refusal'
import { useRuleEditor } from './useRuleEditor'

function Row({ label, value }: { label: string; value: string }) {
	return (
		<div className="flex items-center justify-between py-1 text-sm">
			<dt className="text-ds-text-muted">{label}</dt>
			<dd className="font-medium text-ds-text">{value}</dd>
		</div>
	)
}

interface HeaderActionsProps {
	editing: boolean
	archived: boolean
	onEdit: () => void
	onArchive: () => void
	/** Writing a rule is administration, so both controls are refused below ADMIN. */
	canEdit: boolean
	onCancelEdit: () => void
	onSave: () => void
}

function HeaderActions({ editing, archived, onEdit, onArchive, onCancelEdit, onSave, canEdit }: HeaderActionsProps) {
	if (editing)
		return (
			<>
				<Button variant="secondary" className="rounded-ds-full" onPress={onCancelEdit}>
					Cancel
				</Button>
				<Button className="gap-1.5 rounded-ds-full" onPress={onSave}>
					Save
				</Button>
			</>
		)
	return (
		<>
			{!archived ? (
				<Restricted allowed={canEdit} why={REFUSAL.writeRule}>
					<Button
						variant="secondary"
						className="gap-1.5 rounded-ds-full border-ds-warning bg-ds-warning text-ds-text hover:bg-ds-warning/90"
						onPress={onArchive}
					>
						<Archive className="h-4 w-4" /> Archive
					</Button>
				</Restricted>
			) : null}
			<Restricted allowed={canEdit} why={REFUSAL.writeRule}>
				<Button
					variant="secondary"
					className="gap-1.5 rounded-ds-full border-ds-info bg-ds-info text-ds-primary-foreground hover:bg-ds-info/90"
					onPress={onEdit}
				>
					<Pencil className="h-4 w-4" /> Edit
				</Button>
			</Restricted>
		</>
	)
}

interface ReadOnlyDetailsProps {
	rule: Rule
	axisLabels: Record<string, string>
	valueLabels: Record<string, string>
	empty: boolean
}

function ReadOnlyDetails({ rule, axisLabels, valueLabels, empty }: ReadOnlyDetailsProps) {
	// Rendered in the authoring order, not the order stored on the row, so the same
	// selection always reads the same way.
	const elementTypes = RULE_ELEMENT_TYPE_ORDER.filter((t) => (rule.appliesTo ?? []).includes(t))
	return (
		<div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
			<SectionCard title="Check definition">
				<p className="text-sm leading-relaxed text-ds-text">{rule.checkDefinition || '—'}</p>
				<div className="mt-4">
					<Row label="Validation method" value={rule.validationMethod} />
				</div>
			</SectionCard>
			<SectionCard title="Applies to">
				{empty ? (
					<p className="font-mono text-xs text-ds-text-subtle">Applies to all — {scopeLabel(rule.classification)}</p>
				) : (
					<ChipSet tuple={rule.classification} axisLabels={axisLabels} />
				)}
				<p className="mt-2 text-sm text-ds-text-muted">{scopeLabel(rule.classification, valueLabels)}</p>

				{/* Element types sit in this card, beside the classification tuple: together they
				    are the two halves of what a rule covers — the tuple is WHERE it applies, the
				    element types are WHAT it governs. Chips, not a label/value row, so neither
				    half reads as the more important one. */}
				<div className="mt-3 border-t border-ds-border pt-3">
					<Eyebrow className="mb-2">Element types</Eyebrow>
					{elementTypes.length === 0 ? (
						<p className="font-mono text-xs text-ds-text-subtle">All element types</p>
					) : (
						<div className="flex flex-wrap gap-1.5">
							{/* `muted` (bordered, muted text), not `secondary`: secondary pairs a light
							    background with light foreground text and reads as disabled. This also
							    matches the classification chips directly above. */}
							{elementTypes.map((t) => (
								<Chip key={t} variant="muted" size="sm">
									{RULE_ELEMENT_TYPE_LABEL[t]}
								</Chip>
							))}
						</div>
					)}
				</div>

				<dl className="mt-4 border-t border-ds-border pt-3">
					<Row label="Lifecycle" value={RULE_LIFECYCLE_LABEL[rule.lifecycle]} />
					<Row label="Effective from" value={rule.effectiveFrom} />
					<Row label="Effective to" value={rule.effectiveTo ?? 'Open-ended'} />
				</dl>
			</SectionCard>
		</div>
	)
}

export function RuleDetailPage() {
	const { id } = useParams<{ id: string }>()
	const { data: rule, isLoading } = useRule(id ?? null)
	const facets = railAxesToFacets(useAxesWithValues().axes)

	if (isLoading) return <ListSkeleton label="Loading…" />
	if (!rule) return <p className="text-ds-text-muted">Rule not found.</p>

	return <RuleDetailView rule={rule} facets={facets} />
}

/**
 * The loaded page. Split from the route component so the rule is non-null by
 * construction, which `useRuleEditor` needs (a hook cannot sit behind an early return).
 */
function RuleDetailView({ rule, facets }: { rule: Rule; facets: Facet[] }) {
	const editor = useRuleEditor(rule)
	const { canConfigure } = useCan()
	const { editing, form, pendingAction } = editor

	const axisLabels = Object.fromEntries(facets.map((f) => [f.axis, f.label]))
	const valueLabels = Object.fromEntries(facets.flatMap((f) => f.values.map((v) => [v.code, v.label])))
	const empty = Object.values(rule.classification).every((vs) => vs.length === 0)
	const archived = rule.lifecycle === RuleLifecycle.RETIRED

	return (
		<div>
			{/* A rule is reached from the rules list, from search, and from the applicable-rules
			    card on any record it governs, so back means the previous view. */}
			<BackLink
				fallbackHref="/rules"
				className="mb-4 inline-flex items-center gap-1 text-ds-sm text-ds-text-muted hover:text-ds-text"
			/>
			<PageHeader
				eyebrow={`Rule · ${rule.id} · ${ruleTypeLabel(rule.type)}`}
				title={rule.statement}
				actions={
					<HeaderActions
						editing={editing}
						archived={archived}
						onEdit={editor.startEdit}
						onArchive={() => editor.setPendingAction('archive')}
						onCancelEdit={editor.cancelEdit}
						canEdit={canConfigure}
						onSave={() => editor.setPendingAction('save')}
					/>
				}
			/>

			{!editing ? (
				<div className="mb-6 -mt-4 flex items-center gap-2">
					<RuleSeverityPill severity={rule.severity} bordered className="h-8 px-3 py-1.5" />
					<Chip variant="muted" className={cn('h-8 px-3 py-1.5 text-ds-sm', RULE_LIFECYCLE_COLOR[rule.lifecycle])}>
						{RULE_LIFECYCLE_LABEL[rule.lifecycle]}
					</Chip>
				</div>
			) : null}

			{editing && form ? (
				<SectionCard title="Edit rule properties">
					<EditRulePropertiesForm value={form} facets={facets} onChange={editor.setForm} />
					{editor.saveFailed ? (
						<p role="alert" className="mt-4 text-ds-sm text-ds-danger">
							Could not save this rule. Check the values and try again.
						</p>
					) : null}
				</SectionCard>
			) : (
				<ReadOnlyDetails rule={rule} axisLabels={axisLabels} valueLabels={valueLabels} empty={empty} />
			)}

			<ConfirmDialog
				open={pendingAction !== null}
				title={pendingAction === 'archive' ? 'Archive this rule?' : 'Save changes?'}
				description={
					pendingAction === 'archive'
						? 'Archived rules are retired and stop being resolved as applicable to any context.'
						: `Save the edited properties for ${rule.id}.`
				}
				confirmLabel={pendingAction === 'archive' ? 'Archive rule' : 'Save changes'}
				submitting={editor.isSaving}
				onCancel={() => editor.setPendingAction(null)}
				onConfirm={pendingAction === 'archive' ? editor.confirmArchive : editor.confirmSave}
			/>
		</div>
	)
}
